import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentTasksApi } from "@/api/agent-tasks";
import { agentChannelsApi } from "@/api/agent-channels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { SCHEDULE_PRESETS, SCHEDULE_TIMEZONES, cronToText } from "@/lib/cron-text";
import { cn } from "@/lib/utils";
import { TASKS_QUERY_ROOT } from "./task-list";

const FIELD = "w-full rounded-md border border-border bg-surface px-3 py-2 text-xs";
const CUSTOM = "custom";

/**
 * The one New Task form, opened from any of the three Tasks views. Given an
 * agentId it creates for that agent; without one (the all-agents view) it
 * asks which agent first. Schedules come from SCHEDULE_PRESETS, so anything
 * it saves has a name in every view, plus "Custom" for any other cron.
 */
export function NewTaskForm({
  agentId: fixedAgentId,
  onDone,
}: {
  agentId?: string;
  onDone: () => void;
}) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [pickedAgentId, setPickedAgentId] = useState("");
  const agentId = fixedAgentId ?? pickedAgentId;

  const [type, setType] = useState<"recurring" | "one_off">("recurring");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [preset, setPreset] = useState(SCHEDULE_PRESETS.find((p) => p.value === "0 9 * * 1-5")!.value);
  const [customCron, setCustomCron] = useState("");
  const [timezone, setTimezone] = useState("Europe/London");
  const [taskPrompt, setTaskPrompt] = useState("");
  const [announceChannelId, setAnnounceChannelId] = useState("");

  const cronExpression = preset === CUSTOM ? customCron.trim() : preset;

  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", agentId],
    queryFn: () => agentChannelsApi.list(agentId),
    enabled: !!agentId,
  });

  const create = useMutation({
    mutationFn: () =>
      agentTasksApi.create({
        agentId,
        companyId: tenantId,
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        cronExpression: type === "recurring" ? cronExpression : undefined,
        timezone,
        taskPrompt: taskPrompt.trim() || undefined,
        announceChannelId: announceChannelId || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASKS_QUERY_ROOT });
      onDone();
    },
  });

  const canSave =
    !!agentId && !!title.trim() && (type === "one_off" || !!cronExpression) && !create.isPending;

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-text-primary">New Task</h3>
        <div className="ml-auto flex gap-1">
          {(["recurring", "one_off"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={cn(
                "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors",
                type === t
                  ? "bg-accent/10 text-accent"
                  : "text-text-muted hover:bg-surface-hover hover:text-text-primary",
              )}
            >
              {t === "recurring" ? "Recurring" : "One-Off"}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {!fixedAgentId && (
          <select value={pickedAgentId} onChange={(e) => setPickedAgentId(e.target.value)} className={FIELD}>
            <option value="">Choose an agent…</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        )}
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className="text-sm" />
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="text-sm"
        />
        {type === "recurring" && (
          <>
            <div className="flex gap-2">
              <select value={preset} onChange={(e) => setPreset(e.target.value)} className={cn(FIELD, "flex-1")}>
                {SCHEDULE_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
                <option value={CUSTOM}>Custom…</option>
              </select>
              <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={cn(FIELD, "w-36")}>
                {SCHEDULE_TIMEZONES.map((z) => (
                  <option key={z.value} value={z.value}>
                    {z.label}
                  </option>
                ))}
              </select>
            </div>
            {preset === CUSTOM && (
              <div>
                <Input
                  value={customCron}
                  onChange={(e) => setCustomCron(e.target.value)}
                  placeholder="Cron, e.g. */5 * * * *"
                  className="font-mono text-xs"
                />
                {customCron.trim() && (
                  <p className="mt-1 text-[11px] text-text-muted">
                    Runs: <span className="text-text-secondary">{cronToText(customCron.trim())}</span>
                  </p>
                )}
              </div>
            )}
          </>
        )}
        <textarea
          value={taskPrompt}
          onChange={(e) => setTaskPrompt(e.target.value)}
          placeholder="Task prompt: what should the agent do when this task runs?"
          className={cn(FIELD, "min-h-[72px] resize-y")}
        />
        <div>
          <label className="mb-1 block text-[11px] text-text-muted">Send results to</label>
          <select
            value={announceChannelId}
            onChange={(e) => setAnnounceChannelId(e.target.value)}
            className={FIELD}
            disabled={!agentId}
          >
            <option value="">Task Reports (default)</option>
            {channels.map((ch) => (
              <option key={ch.id} value={ch.id}>
                #{ch.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {create.isError && (
        <p className="mt-2 text-xs text-red-500">
          {create.error instanceof Error ? create.error.message : "Could not create the task"}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" disabled={!canSave} onClick={() => create.mutate()}>
          {create.isPending ? "Creating…" : "Create Task"}
        </Button>
      </div>
    </div>
  );
}
